import isEmpty from 'lodash/isEmpty';

declare global {
  interface Window {
    desktop?: {
      mode: string;
      openLinkInBrowser: (url: string) => void;
      triggerSystemNotification?: (options: Notification) => void;
      handleSystemNotificationAction?: (
        callback: (notification: Notification, action: number) => void
      ) => void;
      handleSystemNotificationClick?: (
        callback: (notification: Notification) => void
      ) => void;
      removeSystemNotificationListeners?: () => void;
      changeMode?: (mode: string, initialPath?: string) => void;
      recording?: {
        start: (meetingUuid?: string) => Promise<DesktopRecordingResult>;
        stop: (
          options?: DesktopRecordingStopOptions
        ) => Promise<DesktopRecordingResult>;
        pause?: () => Promise<DesktopRecordingResult>;
        resume?: () => Promise<DesktopRecordingResult>;
        status: () => Promise<DesktopRecordingStatus>;
      };
    };
  }
}

export interface DesktopRecordingResult {
  ok: boolean;
  sessionId?: string;
  meetingUuid?: string;
  localAudioPath?: string;
  durationMs?: number;
  bytes?: number;
  errorCode?: string;
  errorMessage?: string;
}

export interface DesktopRecordingStatus {
  state: string;
  sessionId?: string;
  localAudioPath?: string;
  meetingUuid?: string | null;
  /** Elapsed capture time when the desktop daemon reports an active session. */
  durationMs?: number;
  /** Epoch ms or ISO start time for the active session, when known. */
  startedAt?: number | string;
}

/** Distinct from idle: status IPC failed; UI should keep its local session. */
export const DESKTOP_RECORDING_STATUS_UNAVAILABLE = 'unavailable';

/** Mirrors Electron `AVOMA_DESKTOP_RECORDING_CHANGED_EVENT`. */
export const AVOMA_DESKTOP_RECORDING_CHANGED_EVENT =
  'avoma-desktop-recording-changed';

export interface DesktopRecordingStopOptions {
  /**
   * When true, Electron skips the "Stop recording?" guardrail.
   * Leave false/omitted for user-initiated Zen / FE stop so the desktop modal shows.
   */
  skipConfirm?: boolean;
  navigateToMeeting?: boolean;
}

const notDesktopRecordingResult = (): DesktopRecordingResult => ({
  ok: false,
  errorCode: 'NOT_DESKTOP',
  errorMessage:
    'Desktop recording is only available in the Avoma desktop app.'
});

const toRecordingFailure = (
  error: unknown,
  errorCode: string,
  fallbackMessage: string
): DesktopRecordingResult => ({
  ok: false,
  errorCode,
  errorMessage: error instanceof Error ? error.message : fallbackMessage
});

export interface Notification {
  id: string;
  title: string;
  subtitle?: string;
  body?: string;
  actions: Array<{ type: string; text: string }>;
}

export const desktopAppModes = {
  ZEN_MODE: 'zen',
  FULL_MODE: 'full'
};

/**
 * Path Electron Zen loads for the latest Minimal UI (Ask Avoma card, row
 * recording actions, etc.). Old Electron builds still load
 * {@link DESKTOP_ZEN_V1_PATH} until users update the desktop app.
 */
export const DESKTOP_ZEN_V2_PATH = 'meetings/views/zen';

/** Legacy Zen list path — old Electron default; serves Minimal V1. */
export const DESKTOP_ZEN_V1_PATH = 'meetings/views/my';

/** Reserved slug under `/meetings/views/:pinnedviewUuid` for Zen V2. */
export const DESKTOP_ZEN_V2_VIEW_SLUG = 'zen';

export const isDesktopContext = () => !isEmpty(window.desktop);

export const isFullMode = (): boolean =>
  window.desktop?.mode === desktopAppModes.FULL_MODE;

export const isZenMode = (): boolean =>
  window.desktop?.mode === desktopAppModes.ZEN_MODE;

/**
 * True when the SPA location is the Zen V2 list route
 * (`/meetings/views/zen` or nested under that slug).
 */
export const isDesktopZenV2Path = (
  pathname: string | undefined | null
): boolean => {
  if (!pathname) return false;
  return (
    pathname === `/${DESKTOP_ZEN_V2_PATH}` ||
    pathname.startsWith(`/${DESKTOP_ZEN_V2_PATH}/`) ||
    pathname === `/meetings/views/${DESKTOP_ZEN_V2_VIEW_SLUG}` ||
    pathname.startsWith(`/meetings/views/${DESKTOP_ZEN_V2_VIEW_SLUG}/`)
  );
};

export const canRecordFromDesktop = (): boolean =>
  typeof window.desktop?.recording?.start === 'function';

/**
 * Invokes the desktop recorder over IPC. Passing `meetingUuid` attaches the
 * capture to an existing meeting (the row case); omitting it lets the desktop
 * app create a fresh meeting shell (the impromptu / top-record case).
 * Returns a structured result even when not running inside the desktop app, so
 * callers can surface a friendly message instead of throwing.
 */
export const startDesktopRecording = async (
  meetingUuid?: string
): Promise<DesktopRecordingResult> => {
  if (!canRecordFromDesktop()) {
    return notDesktopRecordingResult();
  }
  try {
    return await window.desktop!.recording!.start(meetingUuid);
  } catch (error) {
    return toRecordingFailure(
      error,
      'RECORDING_START_FAILED',
      'Failed to start recording.'
    );
  }
};

export const canPauseDesktopRecording = (): boolean =>
  typeof window.desktop?.recording?.pause === 'function';

export const canResumeDesktopRecording = (): boolean =>
  typeof window.desktop?.recording?.resume === 'function';

export const canStopDesktopRecording = (): boolean =>
  typeof window.desktop?.recording?.stop === 'function';

export const canGetDesktopRecordingStatus = (): boolean =>
  typeof window.desktop?.recording?.status === 'function';

export const getDesktopRecordingStatus =
  async (): Promise<DesktopRecordingStatus> => {
    if (!canGetDesktopRecordingStatus()) {
      return { state: 'idle' };
    }
    try {
      return await window.desktop!.recording!.status();
    } catch {
      return { state: DESKTOP_RECORDING_STATUS_UNAVAILABLE };
    }
  };

export const pauseDesktopRecording =
  async (): Promise<DesktopRecordingResult> => {
    if (!canPauseDesktopRecording()) {
      return notDesktopRecordingResult();
    }
    try {
      return await window.desktop!.recording!.pause!();
    } catch (error) {
      return toRecordingFailure(
        error,
        'RECORDING_PAUSE_FAILED',
        'Failed to pause recording.'
      );
    }
  };

export const resumeDesktopRecording =
  async (): Promise<DesktopRecordingResult> => {
    if (!canResumeDesktopRecording()) {
      return notDesktopRecordingResult();
    }
    try {
      return await window.desktop!.recording!.resume!();
    } catch (error) {
      return toRecordingFailure(
        error,
        'RECORDING_RESUME_FAILED',
        'Failed to resume recording.'
      );
    }
  };

/**
 * Stops desktop capture. By default Electron shows the same "Stop recording?"
 * guardrail used by the recording widget. Pass `{ skipConfirm: true }` only
 * for non-interactive / crash paths that must not prompt.
 */
export const stopDesktopRecording = async (
  options: DesktopRecordingStopOptions = {}
): Promise<DesktopRecordingResult> => {
  if (!canStopDesktopRecording()) {
    return notDesktopRecordingResult();
  }
  try {
    return await window.desktop!.recording!.stop(options);
  } catch (error) {
    return toRecordingFailure(
      error,
      'RECORDING_STOP_FAILED',
      'Failed to stop recording.'
    );
  }
};
